import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Star, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import { IMG_SUPPORT2 } from "../mock";
import { api, mediaUrl } from "../api";
import { useApp } from "../context/AppContext";
import { CONTACT_TEXT, waUrl } from "../waHelper";

export default function Support() {
  const { settings, faqs, reviews, refreshReviews, contacts, siteImages, t } = useApp();
  const supportImg = mediaUrl(siteImages.support) || IMG_SUPPORT2;
  const [rev, setRev] = useState({ name: "", city: "", text: "", rating: 5 });

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rev.name || !rev.text) { toast.error("Please add your name and review"); return; }
    await api.addReview(rev);
    await refreshReviews();
    toast.success("Thank you! Your review has been submitted.");
    setRev({ name: "", city: "", text: "", rating: 5 });
  };

  const reviews_list = reviews;

  return (
    <div>
      <section className="relative grid-bg border-b border-black/10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-20 relative animate-fade-up">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">{t("sup.eyebrow")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-3">{t("sup.title")}</h1>
          <p className="text-neutral-600 mt-4 max-w-2xl text-lg leading-relaxed">{t("sup.subtitle")}</p>
        </div>
      </section>

      {/* Contact cards */}
      <section className="max-w-7xl mx-auto px-5 py-14 grid md:grid-cols-3 gap-6">
        <a data-testid="support-whatsapp-link" href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer" className="glass card-hover rounded-2xl p-7 block">
          <div className="w-12 h-12 rounded-xl orange-gradient grid place-items-center mb-4"><MessageCircle className="w-6 h-6 text-white" /></div>
          <h3 className="font-display font-semibold text-lg">WhatsApp</h3>
          <p className="text-neutral-600 text-sm mt-1">Fastest way to reach us</p>
          <p data-testid="support-whatsapp-number" className="text-orange-500 text-sm mt-3 font-medium">+{settings.whatsapp}</p>
        </a>
        <a data-testid="support-phone-link" href={`tel:+${settings.whatsapp}`} className="glass card-hover rounded-2xl p-7 block">
          <div className="w-12 h-12 rounded-xl glass grid place-items-center mb-4"><Phone className="w-6 h-6 text-orange-500" /></div>
          <h3 className="font-display font-semibold text-lg">Call Us</h3>
          <p className="text-neutral-600 text-sm mt-1">Mon–Sun, 8am–10pm</p>
          <p data-testid="support-phone-number" className="text-orange-500 text-sm mt-3 font-medium">+{settings.whatsapp}</p>
        </a>
        <a href={`mailto:${settings.email}`} className="glass card-hover rounded-2xl p-7 block">
          <div className="w-12 h-12 rounded-xl glass grid place-items-center mb-4"><Mail className="w-6 h-6 text-orange-500" /></div>
          <h3 className="font-display font-semibold text-lg">Email</h3>
          <p className="text-neutral-600 text-sm mt-1">We reply within hours</p>
          <p className="text-orange-500 text-sm mt-3 font-medium break-all">{settings.email}</p>
        </a>
      </section>

      {/* Customer service numbers */}
      {contacts.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pb-4">
          <h2 className="font-display font-bold text-2xl mb-6">Our customer-service numbers</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {contacts.map((ct) => (
              <a data-testid={`support-contact-${ct.id}`} key={ct.id} href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer" className="glass card-hover rounded-2xl p-6 block">
                <div className="w-11 h-11 rounded-xl orange-gradient grid place-items-center mb-3"><MessageCircle className="w-5 h-5 text-white" /></div>
                <p className="font-display font-semibold">{ct.label}</p>
                <p data-testid={`support-contact-number-${ct.id}`} className="text-orange-600 text-sm mt-1 font-medium">+{settings.whatsapp}</p>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 py-10">
        <h2 className="font-display font-bold text-3xl text-center mb-8">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="glass rounded-2xl px-6">
          {faqs.map((f, i) => (
            <AccordionItem key={f.id || i} value={`item-${i}`} className="border-black/10">
              <AccordionTrigger className="text-left hover:no-underline hover:text-orange-500 font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-5 py-14 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Leave a Review</span>
          <h2 className="font-display font-bold text-3xl mt-3">Share your experience</h2>
          <p className="text-neutral-600 mt-3">Your feedback helps other families ship with confidence.</p>
          <form onSubmit={submitReview} className="glass rounded-2xl p-6 mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Your name" value={rev.name} onChange={(e) => setRev({ ...rev, name: e.target.value })} className="bg-black/[0.03] border-black/10" />
              <Input placeholder="City, Country" value={rev.city} onChange={(e) => setRev({ ...rev, city: e.target.value })} className="bg-black/[0.03] border-black/10" />
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRev({ ...rev, rating: n })}>
                  <Star className={`w-6 h-6 ${n <= rev.rating ? "fill-orange-500 text-orange-500" : "text-neutral-300"}`} />
                </button>
              ))}
            </div>
            <Textarea placeholder="Tell us about your experience…" value={rev.text} onChange={(e) => setRev({ ...rev, text: e.target.value })} className="bg-black/[0.03] border-black/10 min-h-28" />
            <Button type="submit" className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Send className="w-4 h-4" /> Submit Review</Button>
          </form>
        </div>
        <div>
          <h3 className="font-display font-semibold text-xl mb-4">Recent reviews</h3>
          {reviews_list.length ? (
            <div className="space-y-4 max-h-[520px] overflow-auto pr-1">
              {reviews_list.map((r) => (
                <div key={r.id} className="glass rounded-2xl p-5">
                  <div className="flex gap-0.5 mb-2">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />)}</div>
                  <p className="text-sm text-neutral-700 leading-relaxed">"{r.text}"</p>
                  <p className="text-sm font-semibold mt-3">{r.name}<span className="text-neutral-500 font-normal"> · {r.city || "Customer"}</span></p>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass rounded-2xl p-8 text-center">
              <img src={supportImg} alt="Support" className="w-full h-40 object-cover rounded-xl mb-4 opacity-80" />
              <p className="text-neutral-600 text-sm">Be the first to leave a review!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
